import { debounce, includes, without } from 'lodash-es';

import md5 from 'blueimp-md5';
import Time from '@article/time';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import { trackEvent, getSessionValue } from '../commons/utils.js';
import CountryUtils from '../commons/CountryUtils.js';
import Tracking from '../commons/Tracking.js';

export const Step = {
  CART: 'cart',
  ADDRESS: 'address',
  SHIPPING: 'shipping',
  PAYMENT: 'payment',

  split: () => [Step.CART, Step.ADDRESS, Step.SHIPPING, Step.PAYMENT],
  combined: () => [Step.CART, Step.ADDRESS, Step.PAYMENT],
  steps: (splittable) => (splittable ? Step.split() : Step.combined()),

  previousSteps: (step, splittable) => Step.steps(splittable).splice(0, Step.steps(splittable).indexOf(step)),

  stepNumber: (step, splittable) => {
    const index = Step.steps(splittable).indexOf(step);
    return index >= 0 ? index + 1 : null;
  },

  next: (currentStep, completedSteps, splittable) => {
    const remainingSteps = without(Step.steps(splittable), ...completedSteps);
    return remainingSteps.shift() || Step.PAYMENT;
  },
};

export const ValidationGroup = {
  ADDRESS: [
    'checkoutInfo.email',
    'checkoutInfo.phone',
    'checkoutInfo.shippingFirstName',
    'checkoutInfo.shippingLastName',
    'checkoutInfo.shippingAddress1',
    'checkoutInfo.shippingCountryId',
    'checkoutInfo.shippingRegionId',
    'checkoutInfo.shippingZip',
    'checkoutInfo.shippingCity',
    'checkoutInfo.clientEmail',
    'checkoutInfo.clientPhone',
    'checkoutInfo.shippingInstruction',
  ],
  SHIPPING: ['checkoutInfo.shippingOptionIds'],
  PAYMENT: [
    'checkoutInfo.ccName',
    'checkoutInfo.card',
    'checkoutInfo.billingAddress1',
    'checkoutInfo.billingCountryId',
    'checkoutInfo.billingRegionId',
    'checkoutInfo.billingZip',
    'checkoutInfo.billingCity',
  ],
};

export const ValidationNames = {
  'checkoutInfo.email': 'contact email',
  'checkoutInfo.phone': 'contact phone number',
  'checkoutInfo.shippingFirstName': 'shipping first name',
  'checkoutInfo.shippingLastName': 'shipping last name',
  'checkoutInfo.shippingAddress1': 'shipping street address',
  'checkoutInfo.shippingCountryId': 'shipping country',
  'checkoutInfo.shippingRegionId': 'shipping state',
  'checkoutInfo.shippingZip': 'shipping zip',
  'checkoutInfo.shippingCity': 'shipping city',
  'checkoutInfo.ccName': 'credit card name',
  'checkoutInfo.card': 'credit card',
  'checkoutInfo.billingAddress1': 'billing street address',
  'checkoutInfo.billingCountryId': 'billing country',
  'checkoutInfo.billingRegionId': 'billing state',
  'checkoutInfo.billingZip': 'billing zip',
  'checkoutInfo.billingCity': 'billing city',
  'checkoutInfo.shippingInstruction': 'delivery instructions',
};

export const ActionRequired = {
  ERROR: 'error',
  STEP: 'step',
};

export const PaymentType = {
  AFFIRM: 'AFFIRM',
  KLARNA: 'KLARNA',
  PAYPAL: 'PAYPAL',
  PAYBRIGHT: 'PAYBRIGHT',
  CC_STRIPE: 'CC_STRIPE',
  GOOGLE_PAY: 'GOOGLE_PAY',
  APPLE_PAY: 'APPLE_PAY',
  RESOLVE_PAY: 'RESOLVE_PAY',
};

export function formatCartInfo(cartInfo) {
  if (cartInfo && cartInfo.summary && cartInfo.summary.discounts && cartInfo.summary.discounts.length) {
    cartInfo.summary.discounts.forEach((discount) => {
      discount.endDate = Time.renderDate(discount.endDate, 'MD');
    });
  }

  return cartInfo;
}

export function formatMoneyInfo(moneyInfo) {
  if (moneyInfo) {
    return formatMoney(moneyInfo.currencySymbol, moneyInfo.amount);
  }
  return null;
}

export function formatCart(cart, previousCart) {
  if (cart && cart.success) {
    cart.cartInfo = formatCartInfo(cart.cartInfo);
    // eslint-disable-next-line prefer-destructuring
    cart.shippingOptions = cart.cartShippingOptions[0];
  }
  if (cart && !cart.success) {
    cart.errors = { ...cart.errors, server: cart.message };
  }
  return cart;
}

function getDollarAmount(cents) {
  return (Math.abs(cents) / 100).toFixed(2).replace('.00', '');
}

function getMoney(currency, cents, amount) {
  const sign = cents < 0 ? '- ' : '';
  return `${sign}${currency}${amount}`;
}

export function formatMoney(currency, cents) {
  const amount = getDollarAmount(cents);
  return getMoney(currency, cents, amount);
}

export function formatAndRoundUpMoney(currency, cents) {
  const amount = Math.ceil(getDollarAmount(cents));
  return getMoney(currency, cents, amount);
}

export function formatAndRoundDownMoney(currency, cents) {
  const amount = Math.floor(getDollarAmount(cents));
  return getMoney(currency, cents, amount);
}

export function getLocationLabels(countryId) {
  if (CountryUtils.CA.id === countryId) {
    return {
      region: 'Province',
      zip: 'Postal Code',
    };
  }
  if (CountryUtils.US.id === countryId) {
    return {
      region: 'State',
      zip: 'Zip',
    };
  }
  return {
    region: 'State/Province',
    zip: 'Zip/Postal Code',
  };
}

function formatOrderItem(item) {
  return {
    name: item.productTitle,
    id: item.productSkuNo,
    price: item.price,
    category: item.categoryPath,
    quantity: item.quantity,
  };
}

function isProductOnSale(item) {
  return item.isOnSale;
}

export function getSessionCart() {
  const cart = getSessionValue('cart');
  if (cart) {
    const cartObj = JSON.parse(cart);
    return cartObj;
  }
  return null;
}

export function trackCartError(errors, exception) {
  const MAX_EXCEPTION_LENGTH = 200;
  try {
    let exceptionMessage = null;
    try {
      if (exception) {
        exceptionMessage = exception.toString().trim();
        if (exceptionMessage.length > MAX_EXCEPTION_LENGTH) {
          exceptionMessage = `${exceptionMessage.substring(0, MAX_EXCEPTION_LENGTH)}...`;
        }
      }
    } catch (err) {
      console.error(err);
    }

    if (errors && exceptionMessage) {
      Tracking.track('Cart Error', { Error: JSON.stringify(errors), Exception: exceptionMessage });
    } else if (errors) {
      Tracking.track('Cart Error', { Error: JSON.stringify(errors) });
    }
  } catch (e) {
    // Just log the error - we don't really care if this tracking fails
    console.error(e);
  }
}

export const trackCartEvent = debounce((cartInfo) => {
  const orderItems = cartInfo && cartInfo.summary && cartInfo.summary.items ? cartInfo.summary.items : null;

  if (!orderItems) {
    return;
  }

  const eventId = md5(
    cartInfo && cartInfo.orderInfos && cartInfo.orderInfos[0] && cartInfo.orderInfos[0].purchase && cartInfo.orderInfos[0].purchase.customer
      ? `${cartInfo.orderInfos[0].purchase.customer.email}_${orderItems.map((item) => item.productSkuNo).join(',')}`
      : 'N/A_X_X',
  );

  const cart = {
    currencyCode: cartInfo.countryCode === 'CA' ? 'CAD' : 'USD',
    products: orderItems.map(formatOrderItem),
    productsOnSale: orderItems.filter(isProductOnSale).map((item) => item.productSkuNo),
    total: cartInfo.summary.total ? cartInfo.summary.total / 100 : 0,
    orders: cartInfo.orderInfos.map((orderInfo) => ({
      checkoutGroup: orderInfo.checkoutGroup,
      etaStart: orderInfo.etaStart,
      etaEnd: orderInfo.etaEnd,
      items: orderInfo.items.map(formatOrderItem),
      total: orderInfo.total,
    })),
    splittable: cartInfo.splittable,
    hashedId: eventId,
  };

  const analyticItemList = [];
  cartInfo.orderInfos.forEach((orderInfo) => {
    // for each shipment order -> format
    orderInfo.items.forEach((item) => {
      // format each item in shipment and track them all together
      analyticItemList.push({
        id: item.productSkuNo,
        quantity: item.quantity,
        on_sale: item.isOnSale,
        name: item.productTitle,
        eta_start: orderInfo.etaStart ? orderInfo.etaStart.substring(0, 10) : null,
        eta_end: orderInfo.etaEnd ? orderInfo.etaEnd.substring(0, 10) : null,
      });
    });
  });

  const cartAnalytics = {
    // track total subtotal and items in cart
    total: cartInfo.summary.total ? cartInfo.summary.total / 100 : 0,
    subtotal: cartInfo.summary.subtotal ? cartInfo.summary.subtotal / 100 : 0,
    currency: cartInfo.countryCode === 'CA' ? 'CAD' : 'USD',
    item_list: JSON.stringify(analyticItemList),
  };

  trackEvent('Cart', 'Cart Update', '', '', cart);
  Tracking.expose('cart', cart);
  Tracking.track('Cart Update', cartAnalytics);
}, 400);

export function setRemarketingTag(cartInfo) {
  if (cartInfo && cartInfo.summary && cartInfo.summary.items) {
    window.google_tag_params = window.google_tag_params || {};
    window.google_tag_params.ecomm_pagetype = 'cart';
    window.google_tag_params.ecomm_prodid = [cartInfo.summary.items.map((item) => item.productSkuNo).join(',')];

    if (cartInfo.summary.total) {
      window.google_tag_params.ecomm_totalvalue = cartInfo.summary.total / 100;
    }
  }
}
